import { configurationMap } from './auth-configs'
import type { CountryConfig } from './auth-config-types'
import { getIdentityProviderDataAttribute, parseIdentityProviderAttribute } from './identity-provider'
import { getNaEntraCountryConfig, shouldUseNaEntraStaticMaps } from './na-entra-configuration-lookup'

// Cache the default configuration to avoid repeated lookups
const DEFAULT_CONFIG = configurationMap['DEFAULT']['DEFAULT']['DEFAULT']

/**
 * Helper function to safely get configuration with fallback to DEFAULT values
 * @param environment - The environment (e.g., 'Production', 'Preproduction')
 * @param country - The country code (e.g., 'us', 'ca')
 * @param language - The language code (e.g., 'en', 'fr')
 * @returns The configuration for the specified environment, country, and language
 */
export const getConfiguration = (environment: string, country: string, language: string): CountryConfig => {
	const normalizedCountry = country.toUpperCase()
	const normalizedLanguage = language.toLowerCase()

	const identityProvider = parseIdentityProviderAttribute(getIdentityProviderDataAttribute())
	if (shouldUseNaEntraStaticMaps(identityProvider, environment, normalizedCountry)) {
		const entraConfig = getNaEntraCountryConfig(environment, normalizedCountry, normalizedLanguage)
		if (entraConfig) {
			return entraConfig
		}
	}

	const config = configurationMap[environment]?.[normalizedCountry]?.[normalizedLanguage]

	if (config) {
		return config
	}

	// Log warning with details for debugging
	if (process.env.NODE_ENV !== 'production') {
		console.warn(
			`Configuration not found for environment="${environment}", country="${country}", language="${language}". ` +
				`Falling back to default configuration.`
		)
	}

	return DEFAULT_CONFIG
}
